import { COLUMN_MIN_WIDTHS, HEADER_H, PX_PER_DAY, ROW_H, TRAILING_LABEL_PX, type ColumnId, type ZoomLevel } from '../lib/constants'
import type { PrintOptions } from './print-options'

/** CSS px at 96 dpi, portrait; landscape swaps (spec §3). */
export const PAPER_PX = { A4: { w: 794, h: 1123 }, A3: { w: 1123, h: 1587 } } as const
export const PRINT_MARGIN = 38
export const HEADER_BAND_H = 44
export const FOOTER_BAND_H = 28
export const MAX_GRID_FRACTION = 0.55
/**
 * Ceiling on the trailing-label gutter, as a fraction of the timeline slot. The gutter keeps the
 * label drawn right of the last bar from being cut off, but on paper it is taken out of a fixed
 * width — it must never eat more than a quarter of the sheet's timeline.
 */
export const MAX_GUTTER_FRACTION = 0.25

/**
 * Floors the grid may not shrink past on paper. The screen's own minimums, except `health`, `start`
 * and `finish`. On screen `health` is icon-sized because it holds an icon-only badge, while
 * `printCellText` prints the health *label* (`On track`, `Dokončeno`) — that width truncated every
 * cell to `O…`. The two date columns sit at 72 on screen because a reader who finds one clipped can
 * drag it wider; on paper nothing is resizable, and a two-digit-day/month date (`10/12/2025`,
 * `12. 10. 2025`) measures 66 units under `print-text.ts` — with the cell's 12 units of padding
 * taken off, 72 cut every such date and 84 is the first floor that does not.
 */
export const PRINT_MIN_WIDTHS: Record<ColumnId, number> = { ...COLUMN_MIN_WIDTHS, health: 84, start: 84, finish: 84 }

/**
 * Fit `columns` into `maxWidth` (spec §3). A flat proportional squeeze took the narrow columns
 * below the width their content needs, so shrinking runs in passes: scale proportionally, pin every
 * column that landed under its minimum, then re-share the remaining budget among the rest — until a
 * pass pins nothing new. When the minimums alone do not fit, every minimum is scaled down by the
 * same factor (the grid stays inside `maxWidth`; the cells truncate).
 */
export const fitColumnWidths = (
  columns: ColumnId[],
  widths: Record<ColumnId, number>,
  maxWidth: number,
): Record<ColumnId, number> => {
  const out = { ...widths }
  const raw = columns.reduce((s, c) => s + widths[c], 0)
  if (raw <= maxWidth) return out

  const minSum = columns.reduce((s, c) => s + PRINT_MIN_WIDTHS[c], 0)
  if (minSum >= maxWidth) {
    const shrink = maxWidth / minSum
    columns.forEach((c) => { out[c] = PRINT_MIN_WIDTHS[c] * shrink })
    return out
  }

  const pinned: Record<string, boolean> = {}
  let free = columns.slice()
  let budget = maxWidth
  for (;;) {
    const freeRaw = free.reduce((s, c) => s + widths[c], 0)
    const factor = freeRaw > 0 ? budget / freeRaw : 0
    const next = free.filter((c) => widths[c] * factor < PRINT_MIN_WIDTHS[c])
    if (next.length === 0) {
      free.forEach((c) => { out[c] = widths[c] * factor })
      return out
    }
    next.forEach((c) => { pinned[c] = true; out[c] = PRINT_MIN_WIDTHS[c]; budget -= PRINT_MIN_WIDTHS[c] })
    free = free.filter((c) => !pinned[c])
  }
}

/** One horizontal run of days. `gridWidth` is the grid's width ON THIS STRIP (0 on a continuation
 * strip when columns are not repeated) and `timelineWidth` the rest of the content width. */
export interface PrintStrip { index: number; fromDay: number; toDay: number; gridWidth: number; timelineWidth: number }
export interface PrintPageRef { index: number; rowFrom: number; rowTo: number; strip: number }
export interface PrintLayout {
  pageW: number; pageH: number; margin: number
  bodyX: number; bodyY: number; bodyW: number; bodyH: number
  scale: number; contentW: number; contentH: number
  rowsPerPage: number
  columns: ColumnId[]; columnWidths: Record<ColumnId, number>
  gridWidth: number; timelineWidth: number
  pxPerDay: number; zoomForTiers: ZoomLevel; gutterDays: number
  strips: PrintStrip[]; pages: PrintPageRef[]
}

export interface PrintLayoutInput { rowCount: number; columns: ColumnId[]; columnWidths: Record<ColumnId, number>; totalDays: number }

/**
 * The whole pagination in one pure function (spec §3): the body is drawn in the screen's own
 * content units (ROW_H, HEADER_H) inside a `scale(opts.scale)` group; pages are ordered down
 * (row chunks) then across (timeline strips).
 */
export const buildPrintLayout = (opts: PrintOptions, input: PrintLayoutInput): PrintLayout => {
  const base = PAPER_PX[opts.paper]
  const pageW = opts.orientation === 'landscape' ? base.h : base.w
  const pageH = opts.orientation === 'landscape' ? base.w : base.h
  const margin = PRINT_MARGIN
  const bodyX = margin
  const bodyY = margin + HEADER_BAND_H
  const bodyW = pageW - 2 * margin
  const bodyH = pageH - 2 * margin - HEADER_BAND_H - FOOTER_BAND_H
  const scale = opts.scale
  const contentW = bodyW / scale
  const contentH = bodyH / scale
  const rowsPerPage = Math.max(1, Math.floor((contentH - HEADER_H) / ROW_H))

  // The screen sizes `health` for an icon; paper prints the label, so widen it before fitting.
  const requested = { ...input.columnWidths }
  if (input.columns.indexOf('health') >= 0) requested.health = Math.max(requested.health, PRINT_MIN_WIDTHS.health)
  const maxGrid = contentW * MAX_GRID_FRACTION
  const columnWidths = fitColumnWidths(input.columns, requested, maxGrid)
  const gridWidth = input.columns.reduce((s, c) => s + columnWidths[c], 0)
  const timelineWidth = contentW - gridWidth

  const totalDays = Math.max(1, input.totalDays)
  // Fit means fit: unlike the screen (`pxPerDayFor`), paper has no `MIN_PX_PER_DAY` floor to fall
  // back on, because there is nothing to scroll — a floor just pushes the tail of a long range off
  // the sheet. A 1 700-day range needs ~0.23 units/day here; the screen would have clamped to 0.5
  // and drawn 1 048 units into a 589-unit slot.
  const gutter = Math.min(TRAILING_LABEL_PX, timelineWidth * MAX_GUTTER_FRACTION)
  const pxPerDay = opts.zoom === 'fit' ? Math.max(0, timelineWidth - gutter) / totalDays : PX_PER_DAY[opts.zoom]
  const zoomForTiers: ZoomLevel = opts.zoom === 'fit' ? 'project' : opts.zoom
  const gutterDays = Math.ceil((opts.zoom === 'fit' ? gutter : TRAILING_LABEL_PX) / pxPerDay)
  // Strips: the first one always carries the grid; with `repeatColumns` off every later strip drops
  // it and spends the whole content width on the timeline, so a wide timeline needs fewer pages
  // across. Fit zoom is a single strip and never reaches the second branch.
  const strips: PrintStrip[] = []
  if (opts.zoom === 'fit') {
    strips.push({ index: 0, fromDay: 0, toDay: totalDays, gridWidth, timelineWidth })
  } else {
    let from = 0
    while (from < totalDays) {
      const first = strips.length === 0
      const gw = first || opts.repeatColumns ? gridWidth : 0
      const tw = contentW - gw
      const days = Math.max(1, Math.floor(tw / pxPerDay))
      strips.push({ index: strips.length, fromDay: from, toDay: Math.min(totalDays, from + days), gridWidth: gw, timelineWidth: tw })
      from += days
    }
  }

  const rowPages = Math.max(1, Math.ceil(input.rowCount / rowsPerPage))
  const pages: PrintPageRef[] = []
  strips.forEach((s) => {
    for (let r = 0; r < rowPages; r++) {
      pages.push({ index: pages.length, rowFrom: r * rowsPerPage, rowTo: Math.min(input.rowCount, (r + 1) * rowsPerPage), strip: s.index })
    }
  })

  return { pageW, pageH, margin, bodyX, bodyY, bodyW, bodyH, scale, contentW, contentH, rowsPerPage, columns: input.columns.slice(), columnWidths, gridWidth, timelineWidth, pxPerDay, zoomForTiers, gutterDays, strips, pages }
}
